import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Stack,
  Alert,
  Grid,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import PasswordField from './PasswordField';
import apiClient from '../../services/api';

const defaultPerms = {
  can_access_dashboard: false,
  can_manage_posts: false,
  can_manage_doctors: false,
  can_manage_departments: false,
  can_manage_board: false,
  can_manage_team: false,
  can_manage_jobs: false,
  can_view_job_applications: false,
  can_view_contact_messages: false,
  can_manage_settings: false,
  can_manage_users: false,
};

const AdminAccessControl = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [perms, setPerms] = useState(defaultPerms);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // New user form state
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: ''
  });
  const [creatingUser, setCreatingUser] = useState(false);

  // Password reset state
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
  const [passwordResetForm, setPasswordResetForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [resettingPassword, setResettingPassword] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const resp = await apiClient.getUsers();
      if (resp?.success) setUsers(resp.data || []);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    if (!selectedUserId) return;
    fetchUserPermissions(selectedUserId);
  }, [selectedUserId]);

  const fetchUserPermissions = async (userId) => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiClient.getUserPermissions(userId);
      if (resp?.success) setPerms({ ...defaultPerms, ...resp.data });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => (e) => {
    setPerms(prev => ({ ...prev, [key]: e.target.checked }));
  };

  const handleSave = async () => {
    if (!selectedUserId) return;
    setSaved(false);
    setError('');
    try {
      const resp = await apiClient.updateUserPermissions(selectedUserId, perms);
      if (!resp?.success) throw new Error(resp?.error || 'Failed to save');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleNewUserSubmit = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    setError('');

    try {
      // Create user with admin role
      const userData = {
        ...newUserForm,
        role: 'admin' // All users are admins with different permission levels
      };
      
      const resp = await apiClient.createUser(userData);
      if (!resp?.success) throw new Error(resp?.error || 'Failed to create user');

      // Reset form and hide it
      setNewUserForm({
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: ''
      });
      setShowNewUserForm(false);

      // Refresh users list
      await fetchUsers();

      // Show success message
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);

    } catch (e) {
      setError(e.message);
    } finally {
      setCreatingUser(false);
    }
  };

  const handleNewUserInputChange = (field) => (e) => {
    setNewUserForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handlePasswordResetInputChange = (field) => (e) => {
    setPasswordResetForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleOpenPasswordReset = () => {
    setShowPasswordResetDialog(true);
    setPasswordResetForm({ newPassword: '', confirmPassword: '' });
    setPasswordResetError('');
  };

  const handleClosePasswordReset = () => {
    setShowPasswordResetDialog(false);
    setPasswordResetForm({ newPassword: '', confirmPassword: '' });
    setPasswordResetError('');
  };

  const handlePasswordReset = async () => {
    if (!selectedUserId) return;
    
    const { newPassword, confirmPassword } = passwordResetForm;
    
    if (!newPassword || !confirmPassword) {
      setPasswordResetError('Both password fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordResetError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordResetError('Password must be at least 6 characters long');
      return;
    }
    
    setResettingPassword(true);
    setPasswordResetError('');
    
    try {
      const resp = await apiClient.updateUserPassword(selectedUserId, newPassword);
      if (!resp?.success) throw new Error(resp?.error || 'Failed to reset password');
      
      // Close dialog and show success message
      handleClosePasswordReset();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      
    } catch (e) {
      setPasswordResetError(e.message);
    } finally {
      setResettingPassword(false);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUserId(userId);
  };

    const handleDeleteUser = async (userId) => {
    // Use window.confirm instead of global confirm
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      // Note: You'll need to add a delete user endpoint to your API
      // For now, we'll just remove from the local state
      setUsers(prev => prev.filter(u => u.id !== userId));
      if (selectedUserId === userId) {
        setSelectedUserId('');
        setPerms(defaultPerms);
      }
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>Access Control</Typography>

      {/* Success/Error Messages */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Operation completed successfully!</Alert>}

      <Grid container spacing={3}>
        {/* Left Column - User Management */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
                         <Box sx={{ 
               display: 'flex', 
               justifyContent: 'space-between', 
               alignItems: 'center', 
               mb: 3,
               pb: 2,
               borderBottom: '1px solid #e0e0e0'
             }}>
               <Typography variant="h6" sx={{ 
                 display: 'flex', 
                 alignItems: 'center', 
                 gap: 1,
                 fontWeight: 600,
                 color: 'primary.main'
               }}>
                 <PersonIcon sx={{ color: 'primary.main' }} /> User Management
               </Typography>
               <Button
                 variant={showNewUserForm ? "outlined" : "contained"}
                 startIcon={<AddIcon />}
                 onClick={() => setShowNewUserForm(!showNewUserForm)}
                 sx={{
                   borderRadius: 2,
                   textTransform: 'none',
                   fontWeight: 600,
                   px: 3,
                   py: 1
                 }}
               >
                 {showNewUserForm ? 'Cancel' : 'Add New Admin'}
               </Button>
             </Box>

                         {/* New User Form */}
             {showNewUserForm && (
               <Card sx={{ 
                 mb: 3, 
                 border: '1px solid #e0e0e0',
                 boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                 borderRadius: 2
               }}>
                 <CardContent sx={{ p: 3 }}>
                   <Box sx={{ 
                     display: 'flex', 
                     alignItems: 'center', 
                     gap: 1, 
                     mb: 3,
                     pb: 2,
                     borderBottom: '1px solid #e0e0e0'
                   }}>
                     <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                       Create New Admin User
                     </Typography>
                   </Box>
                   
                                                           <form onSubmit={handleNewUserSubmit}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Username and Email Row */}
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          <TextField
                            label="Username"
                            value={newUserForm.username}
                            onChange={handleNewUserInputChange('username')}
                            size="medium"
                            sx={{ 
                              flex: '1 1 200px',
                              minWidth: '200px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                }
                              }
                            }}
                          />
                          <TextField
                            label="Email"
                            type="email"
                            value={newUserForm.email}
                            onChange={handleNewUserInputChange('email')}
                            size="medium"
                            sx={{ 
                              flex: '1 1 200px',
                              minWidth: '200px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                }
                              }
                            }}
                          />
                        </Box>

                        {/* Password Field */}
                        <PasswordField
                          label="Password"
                          value={newUserForm.password}
                          onChange={handleNewUserInputChange('password')}
                          required
                          size="medium"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              }
                            }
                          }}
                        />

                        {/* First Name and Last Name Row */}
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          <TextField
                            label="First Name"
                            value={newUserForm.firstName}
                            onChange={handleNewUserInputChange('firstName')}
                            size="medium"
                            sx={{ 
                              flex: '1 1 200px',
                              minWidth: '200px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                }
                              }
                            }}
                          />
                          <TextField
                            label="Last Name"
                            value={newUserForm.lastName}
                            onChange={handleNewUserInputChange('lastName')}
                            size="medium"
                            sx={{ 
                              flex: '1 1 200px',
                              minWidth: '200px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                }
                              }
                            }}
                          />
                        </Box>

                        {/* Info Note */}
                        <Box sx={{ 
                          p: 2.5, 
                          bgcolor: 'info.50', 
                          borderRadius: 2, 
                          border: '1px solid #e3f2fd',
                          borderLeft: '4px solid #2196f3'
                        }}>
                          <Typography variant="body2" color="info.main" sx={{ fontWeight: 500 }}>
                            <strong>ℹ️ Note:</strong> New admin users will have no permissions by default. 
                            After creating the user, select them from the list to grant specific permissions.
                          </Typography>
                        </Box>

                        {/* Submit Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={creatingUser}
                            size="large"
                            sx={{
                              px: 4,
                              py: 1.5,
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                              }
                            }}
                          >
                            {creatingUser ? 'Creating...' : 'Create Admin User'}
                          </Button>
                        </Box>
                      </Box>
                   </form>
                 </CardContent>
               </Card>
             )}

            {/* Users List */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mb: 3,
              pt: 2
            }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                Existing Admin Users ({users.length})
              </Typography>
            </Box>

            <List sx={{ 
              maxHeight: 400, 
              overflow: 'auto',
              bgcolor: 'grey.50',
              borderRadius: 2,
              p: 1
            }}>
              {users.map(user => (
                <ListItem
                  key={user.id}
                  sx={{
                    border: selectedUserId === user.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    borderRadius: 2,
                    mb: 1,
                    cursor: 'pointer',
                    bgcolor: selectedUserId === user.id ? 'primary.50' : 'white',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: selectedUserId === user.id ? 'primary.100' : '#f8f9fa',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleSelectUser(user.id)}
                >
                  <ListItemText
                    primary={`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username}
                    secondary={`${user.username} (${user.email}) - Admin`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteUser(user.id);
                      }}
                      size="small"
                      color="error"
                      sx={{
                        '&:hover': {
                          bgcolor: 'error.100',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Right Column - Permissions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SecurityIcon /> User Permissions
            </Typography>

            {!selectedUserId ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <SecurityIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="body1">
                  Select a user from the list to manage their permissions
                </Typography>
              </Box>
            ) : (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Managing permissions for: {users.find(u => u.id === selectedUserId)?.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Check the permissions you want to grant to this user
                  </Typography>
                </Box>

                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={perms.can_access_dashboard} onChange={handleToggle('can_access_dashboard')} />}
                    label="Access Dashboard"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={perms.can_manage_posts} onChange={handleToggle('can_manage_posts')} />}
                    label="Manage Posts"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={perms.can_manage_doctors} onChange={handleToggle('can_manage_doctors')} />}
                    label="Manage Doctors"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={perms.can_manage_departments} onChange={handleToggle('can_manage_departments')} />}
                    label="Manage Departments"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={perms.can_manage_board} onChange={handleToggle('can_manage_board')} />}
                    label="Manage Board of Directors"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={perms.can_manage_team} onChange={handleToggle('can_manage_team')} />}
                    label="Manage Team Members"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={perms.can_manage_jobs} onChange={handleToggle('can_manage_jobs')} />}
                    label="Manage Jobs"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={perms.can_view_job_applications} onChange={handleToggle('can_view_job_applications')} />}
                    label="View Job Applications"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={perms.can_view_contact_messages} onChange={handleToggle('can_view_contact_messages')} />}
                    label="View Contact Messages"
                  />
                                    <FormControlLabel
                    control={<Checkbox checked={perms.can_manage_settings} onChange={handleToggle('can_manage_settings')} />} 
                    label="Manage Settings (maintenance mode)" 
                  />
                  <FormControlLabel 
                    control={<Checkbox checked={perms.can_manage_users} onChange={handleToggle('can_manage_users')} />} 
                    label="Manage Users (create/edit/delete users)" 
                  />
                </FormGroup>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={<EditIcon />}
                  >
                    {loading ? 'Saving...' : 'Save Permissions'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={handleOpenPasswordReset}
                    disabled={!selectedUserId}
                    startIcon={<LockIcon />}
                    color="warning"
                  >
                    Reset Password
                  </Button>
                </Box>
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Password Reset Dialog */}
      <Dialog 
        open={showPasswordResetDialog} 
        onClose={handleClosePasswordReset}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          pb: 1
        }}>
          <LockIcon color="warning" />
          Reset User Password
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Reset password for user: <strong>
                {users.find(u => u.id === selectedUserId)?.username}
              </strong>
            </Typography>
            
            {passwordResetError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordResetError}
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <PasswordField
                label="New Password"
                value={passwordResetForm.newPassword}
                onChange={handlePasswordResetInputChange('newPassword')}
                required
                autoComplete="new-password"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
              
              <PasswordField
                label="Confirm New Password"
                value={passwordResetForm.confirmPassword}
                onChange={handlePasswordResetInputChange('confirmPassword')}
                required
                autoComplete="new-password"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
              
              <Box sx={{ 
                p: 2, 
                bgcolor: 'warning.50', 
                borderRadius: 2, 
                border: '1px solid #fff3cd',
                borderLeft: '4px solid #ffc107'
              }}>
                <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                  <strong>⚠️ Warning:</strong> This action will immediately change the user's password. 
                  Make sure to communicate the new password to the user securely.
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleClosePasswordReset}
            disabled={resettingPassword}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePasswordReset}
            variant="contained"
            color="warning"
            disabled={resettingPassword}
            startIcon={<SaveIcon />}
          >
            {resettingPassword ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminAccessControl;




